import React, { useEffect, useState } from 'react';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import HorizontalDotsButton from '@/components/HorizontalDotsButton/HorizontalDotsButton';
import Dialog from '@/components/Dialog/Dialog';
import Button from '@/components/Button/Button';
import {
	createSequenceTemplate,
	deleteSequence,
	updateSequence
} from '@/services/sequencesService';
import toast from 'react-hot-toast';
import { IdleIcon, PaperIcon, PlayIcon } from '@/resources/assets/images';
import Tooltip from '@/components/Tooltip/Tooltip';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { useSequences, type SequencesType } from '../../hooks/useSequences';
import { useSequencesContext } from '../../SequencesContext';

export default function SequencesList({
	paginatedSequences
}: {
	paginatedSequences: SequencesType[] | undefined;
}) {
	const { sequences } = useSequencesContext();
	const isSequenceEmpty = sequences?.length === 0;

	return (
		<>
			{isSequenceEmpty ? (
				<div className="h-38 bg-dm-black3 px-6 py-4">
					<div className="flex h-full flex-col justify-center gap-1 rounded-2xl bg-dm-black3.5 px-6 py-4">
						<span className="block text-lg font-semibold text-dm-gold">
							You have no sequences yet
						</span>
						<span className="block text-sm text-dm-white">
							Start your first sequence now by clicking the 'New
							Sequence' button.
						</span>
					</div>
				</div>
			) : (
				paginatedSequences?.map(sequence => (
					<SequencesItem key={sequence.id} sequence={sequence} />
				))
			)}
		</>
	);
}

function SequencesItem({ sequence }: { sequence: SequencesType }) {
	const [isEditingName, setIsEditingName] = useState(false);
	const [isDeletingSequence, setIsDeletingSequence] = useState(false);
	const [isDeleteLoading, setIsDeletingLoading] = useState(false);

	const {
		selectedSequence,
		setSelectedSequence,
		setActiveTab,
		setSequencePage,
		setSelectedTemplate,
		setSelectedStep
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const { fetchSequences } = useSequences();

	const isSelected = sequence.id === selectedSequence?.id;

	function handleSelectSequence() {
		setSelectedSequence(sequence);
		setSelectedStep(sequence.steps[0]);
		setActiveTab('SEQUENCE');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: sequence.id,
			eventName: 'user_clicked_on_a_sequence'
		});
	}

	function handleDeleteSequenceOpen() {
		setIsDeletingSequence(!isDeletingSequence);
	}

	function onConfirmDelete() {
		if (!sequence.id) return;
		setIsDeletingLoading(true);
		deleteSequence(sequence.id)
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error deleting sequence! Please try again later.'
					);
				}
				toast.success('Sequence deleted successfully!');
				fetchSequences();
				setIsDeletingSequence(false);
				setIsDeletingLoading(false);
				setSelectedSequence(undefined);
				setActiveTab('SEQUENCE');
			})
			.catch(err => {
				toast.error(err.message);
				setIsDeletingLoading(false);
			});
	}

	function handleEditButton() {
		setSelectedSequence(sequence);
		setSelectedTemplate({
			name: sequence.name,
			steps: sequence.steps
		});
		setSelectedStep(sequence.steps[0]);
		setSequencePage('STEPS-CREATION');
	}

	function handleAddToTemplate() {
		createSequenceTemplate({
			name: `${sequence.name} template`,
			steps: sequence.steps,
			description: ''
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error adding template! Please try again later.'
					);
				}
				toast.success('Template added successfully!');
			})
			.catch(err => {
				toast.error(err.message);
			});
	}

	const dropdownMenuOptions = [
		{ id: '1', label: 'Rename', onClick: () => setIsEditingName(true) },
		{ id: '2', label: 'Edit', onClick: handleEditButton },
		{ id: '3', label: 'Save as a template', onClick: handleAddToTemplate },
		{ id: '4', label: 'Remove', onClick: handleDeleteSequenceOpen }
	];

	return (
		<>
			<Dialog
				isOpen={isDeletingSequence}
				handleOpen={handleDeleteSequenceOpen}
			>
				<span className="mb-2 text-2xl font-medium text-orange">
					Confirm delete
				</span>
				<span className="text-lg text-gray-200">
					Are you sure you want to remove the selected sequence?
				</span>
				<div className="flex justify-between">
					<div className="w-1/3">
						<Button
							variant="ghost"
							onClick={handleDeleteSequenceOpen}
						>
							Cancel
						</Button>
					</div>
					<div className="w-2/5">
						<Button variant="danger" onClick={onConfirmDelete}>
							{isDeleteLoading ? 'Deleting...' : 'Confirm Delete'}
						</Button>
					</div>
				</div>
			</Dialog>
			<div
				style={{
					height: '16.75%',
					minHeight: '95px'
				}}
				className={`flex w-full cursor-pointer border-b py-4 pl-4 pr-0 hover:bg-dm-black3 ${
					isSelected
						? 'border-orange bg-dm-black3'
						: 'border-dark-grey bg-dm-black'
				}`}
				onClick={handleSelectSequence}
			>
				<div className="my-auto flex w-6/12 items-center">
					<NameEdit
						sequence={sequence}
						isEditingName={isEditingName}
						setIsEditingName={setIsEditingName}
					/>
				</div>
				<div className="w-5/12 border-l border-r border-dashed border-dark-grey">
					<div className="mx-auto my-auto grid h-full w-5/6 grid-cols-2 grid-rows-2 text-xs">
						<span className="my-auto">
							Businesses
							<br />
							{sequence.analytics?.totalBusinessCount || 0}
						</span>
						<span className="my-auto">
							Active <br />
							{sequence.analytics?.activeBusinessCount || 0}
						</span>
						<span className="my-auto">
							Open rate <br />
							{sequence.analytics?.openRate || 0}%
						</span>
						<span className="my-auto">
							Replies <br />
							{sequence.analytics?.replyCount || 0}
						</span>
					</div>
				</div>
				<div className="flex w-1/12 items-center justify-center text-white">
					<div className="rounded-lg hover:bg-dark-grey hover:bg-opacity-20">
						<DropdownMenu
							options={dropdownMenuOptions}
							itemClassName="text-white py-3 px-2 hover:bg-dm-black3"
						>
							<HorizontalDotsButton />
						</DropdownMenu>
					</div>
				</div>
			</div>
		</>
	);
}

function NameEdit({
	sequence,
	isEditingName,
	setIsEditingName
}: {
	sequence: SequencesType;
	isEditingName: boolean;
	setIsEditingName: React.Dispatch<React.SetStateAction<boolean>>;
}) {
	const { sequences, setSequences, setSelectedSequence } =
		useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const [sequenceName, setSequenceName] = useState(sequence.name);
	const inputRef = React.useRef<HTMLInputElement>(null);

	function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
		setSequenceName(e.target.value);
	}

	function handleFormSubmit() {
		setIsEditingName(false);

		if (sequenceName === sequence.name || !sequence.id) return;

		updateSequence({
			campaignId: sequence.id,
			name: sequenceName
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error editing sequence name! Please try again later.'
					);
				}
				setSequences(
					sequences?.map(c => {
						if (c.id === sequence.id) {
							return {
								...c,
								name: sequenceName
							};
						}
						return c;
					})
				);
				setSelectedSequence({
					...sequence,
					name: sequenceName
				});
			})
			.catch(err => {
				toast.error(err.message);
				setSequenceName(sequence.name);
			});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: sequence.id,
			eventName: 'user_edited_sequence_name'
		});
	}

	useEffect(() => {
		if (isEditingName && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditingName]);

	return (
		<div className="flex flex-col">
			{isEditingName ? (
				<form onSubmit={handleFormSubmit}>
					<input
						type="text"
						ref={inputRef}
						value={sequenceName}
						onBlur={handleFormSubmit}
						onChange={handleNameChange}
						className="w-3/4 bg-light-grey text-gray-700"
						placeholder="Enter sequence name here"
					/>
				</form>
			) : (
				<>
					<h6 className="text-lg text-orange">{sequenceName}</h6>
					<div className="flex items-center gap-2">
						{sequence.status === 'ACTIVE' && (
							<Tooltip
								content="This sequence is currently in progress and actively engaging businesses."
								longerBubble
								position="right"
							>
								<PlayIcon />
							</Tooltip>
						)}
						{sequence.status === 'DRAFT' && (
							<Tooltip
								content="This is a draft sequence saved from your last edit session. Complete the setup to activate it."
								longerBubble
								position="right"
							>
								<PaperIcon />
							</Tooltip>
						)}
						{sequence.status === 'IDLE' && (
							<Tooltip
								content="This sequence currently has no active processes."
								longerBubble
								position="right"
							>
								<IdleIcon />
							</Tooltip>
						)}
						<span>{sequence.steps.length} steps</span>
					</div>
				</>
			)}
		</div>
	);
}
